import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { useSession } from '@/lib/session';

/**
 * Checking that a pack is genuine.
 *
 * ── WHO VERIFIES, AND WHY IT IS NOT US ──────────────────────────────────────
 *
 * NAFDAC does. Nigerian packs carry their Mobile Authentication Service: a
 * scratch panel over a numeric code, texted to a shortcode, answered by SMS.
 * There is no QR on most packs and no public API ChiCare could call, so a
 * screen here with a "Verify" button that returned GENUINE would be inventing
 * an answer it has no way to know.
 *
 * So this screen does the two honest things: it shows somebody exactly how to
 * ask, and it records what they were told. The second is what makes "NAFDAC
 * verified or you don't pay" a promise with evidence behind it rather than a
 * slogan — nothing had ever written to that table, so the count on the shop
 * front read zero forever.
 *
 * ── WHY IT SAYS NOT EVERY PACK HAS A PANEL ──────────────────────────────────
 *
 * Because plenty of genuine medicines do not carry one yet, and a buyer who
 * expects a panel, finds none, and is told nothing will conclude their medicine
 * is counterfeit. That is a worse failure than not offering the check at all.
 */
export function VerifyPackScreen() {
  const [params] = useSearchParams();
  const { user } = useSession();

  const productId = Number(params.get('product')) || null;
  const subOrder = params.get('order');

  const { data: how, isLoading } = useQuery({
    queryKey: ['verification', 'how'],
    queryFn: ({ signal }) => api.verificationHow(signal),
    staleTime: 60 * 60 * 1000,
  });

  const [result, setResult] = useState<'genuine' | 'fake' | 'no_reply' | null>(null);
  const [code, setCode] = useState('');

  const record = useMutation({
    mutationFn: () =>
      api.recordVerification({
        product_id: productId!,
        result: result!,
        sub_order_reference: subOrder ?? undefined,
        code: code || undefined,
      }),
  });

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-5">
      <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        Check your pack is real
      </h1>
      <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted">
        NAFDAC checks it, not us. It takes about a minute and it is free.
      </p>

      {/* ── How ──────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <Skeleton className="mt-5 h-[180px] w-full" radius="card" />
      ) : how ? (
        <section className="mt-5 rounded-card border border-line bg-white p-4">
          <ol className="m-0 list-none space-y-3 p-0">
            {how.steps.map((step, index) => (
              <li key={step} className="flex gap-2.5">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-blue-tint font-mono text-[11px] font-bold text-blue">
                  {index + 1}
                </span>
                <span className="text-[13px] leading-relaxed text-ink">{step}</span>
              </li>
            ))}
          </ol>

          {/* The shortcode, big and monospace. It is a number somebody copies
              onto a phone keypad while holding a box in the other hand. */}
          <div className="mt-4 rounded-card bg-paper p-3.5 text-center">
            <p className="m-0 text-[11px] uppercase tracking-[0.08em] text-faint">Text the code to</p>
            <p className="m-0 mt-1 font-mono text-[30px] font-bold tracking-[0.1em] text-blue">
              {how.shortcode}
            </p>
            <a
              href={`sms:${how.shortcode}`}
              className="mt-1.5 inline-flex min-h-11 items-center gap-1.5 text-[12.5px] font-semibold text-blue no-underline"
            >
              <Icon name="message" size={14} />
              Open my messages
            </a>
          </div>

          <p className="m-0 mt-3 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-faint">
            <Icon name="alert" size={13} className="mt-px shrink-0" />
            {how.no_panel}
          </p>
        </section>
      ) : null}

      {/* ── What did it say ──────────────────────────────────────────────── */}
      {productId ? (
        <section className="mt-5" data-testid="verify-report">
          {record.data ? (
            <Outcome
              result={record.data.result}
              next={record.data.next}
              startRefund={record.data.start_refund}
              subOrder={subOrder}
            />
          ) : (
            <>
              <h2 className="m-0 font-display text-[15px] font-bold">What did NAFDAC reply?</h2>
              <p className="m-0 mt-1 text-[12px] leading-relaxed text-muted">
                Telling us keeps the count on our home screen honest, and is how we find a bad
                batch before anybody else gets one.
              </p>

              <div className="mt-3 space-y-2">
                {(
                  [
                    ['genuine', 'It said genuine', 'ok'],
                    ['fake', 'It said fake', 'bad'],
                    ['no_reply', 'Nothing came back', 'quiet'],
                  ] as const
                ).map(([value, label, tone]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setResult(value)}
                    aria-pressed={result === value}
                    data-testid={`verify-${value}`}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-2.5 rounded-card border px-3.5 text-left text-[13px] font-semibold',
                      result === value
                        ? tone === 'bad'
                          ? 'border-danger bg-danger-tint text-danger'
                          : 'border-blue bg-blue-tint text-blue'
                        : 'border-line bg-white text-ink hover:border-blue',
                    )}
                  >
                    <Icon
                      name={tone === 'ok' ? 'check' : tone === 'bad' ? 'alert' : 'clock'}
                      size={15}
                      strokeWidth={2.2}
                      className="shrink-0"
                    />
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-3">
                <Input
                  label="The code on the pack (optional)"
                  value={code}
                  inputMode="numeric"
                  maxLength={40}
                  hint="Only if you still have it. It helps us spot the same code being reported twice."
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>

              {!user ? (
                <p className="m-0 mt-3 rounded-card border border-line bg-paper px-3 py-2.5 text-[12px] leading-relaxed text-muted">
                  <Link to="/signin" className="font-semibold text-blue no-underline">
                    Sign in
                  </Link>{' '}
                  to tell us what it said. We ask because an anonymous report could be used to
                  smear a pharmacy that has done nothing wrong.
                </p>
              ) : null}

              {record.error instanceof ApiError ? (
                <p className="m-0 mt-2 text-[12px] font-medium text-danger">
                  {record.error.message}
                </p>
              ) : null}

              <Button
                full
                className="mt-3"
                loading={record.isPending}
                disabled={!result || !user}
                onClick={() => record.mutate()}
              >
                Tell ChiCare
              </Button>
            </>
          )}
        </section>
      ) : (
        <p className="m-0 mt-5 rounded-card border border-line bg-paper px-3 py-2.5 text-[12px] leading-relaxed text-muted">
          Open this from an order to tell us what the reply said.{' '}
          <Link to="/orders" className="font-semibold text-blue no-underline">
            Your orders
          </Link>
        </p>
      )}
    </div>
  );
}

function Outcome({
  result,
  next,
  startRefund,
  subOrder,
}: {
  result: string;
  next: string;
  startRefund: boolean;
  subOrder: string | null;
}) {
  const bad = result === 'fake';

  return (
    <div
      className={cn(
        'rounded-card border p-4',
        bad ? 'border-danger/30 bg-danger-tint' : 'border-verified/30 bg-verified-tint',
      )}
      data-testid="verify-outcome"
    >
      <p
        className={cn(
          'm-0 flex items-center gap-1.5 font-display text-[16px] font-bold',
          bad ? 'text-danger' : 'text-verified',
        )}
      >
        <Icon name={bad ? 'alert' : 'shield'} size={17} filled={!bad} />
        {bad ? 'Do not take it' : result === 'no_reply' ? 'No reply yet' : 'That pack is genuine'}
      </p>

      <p className="m-0 mt-2 text-[13px] leading-relaxed text-ink">{next}</p>

      {/* §4.6 — a suspected counterfeit is refunded first and investigated
          second, so the way to the refund is one tap, not a support email. */}
      {startRefund ? (
        <Link
          to={subOrder ? `/orders/${subOrder}/refund` : '/orders'}
          className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-btn bg-danger px-4 text-[13px] font-bold text-white no-underline"
        >
          Start my refund
          <Icon name="chevron" size={14} strokeWidth={2.4} />
        </Link>
      ) : null}
    </div>
  );
}
